import type {MouseEvent} from 'react';
import {Flex} from '@chakra-ui/layout';
import {Button} from '@chakra-ui/button';
import {MenuList, Menu, MenuButton, MenuItem} from '@chakra-ui/menu';
import {Icon, type IconProps} from '@chakra-ui/icon';

import {useTranslate} from '@eksab/i18n';

interface ChooseLanguageProps {
  languages: Language[];
  selectedLanguage: Language;
  onLanguageChange: (language: Language) => void;
}

export function ChooseLanguage({languages, selectedLanguage, onLanguageChange}: ChooseLanguageProps) {
  const t = useTranslate();

  return (
    <Flex
      gap="4"
      align="center"
      justify="space-between"
      px="3"
      py="2"
      border="1px solid"
      borderColor="#1ba482"
      rounded="md"
      fontSize="sm"
      fontWeight="700"
    >
      {t(languages.length > 1 ? 'trivia.select-language' : 'trivia.language.available-in')}

      {languages.length > 1 ? (
        <LanguageSwitch languages={languages} selectedLanguage={selectedLanguage} onLanguageChange={onLanguageChange} />
      ) : (
        <Flex as="span" lang={selectedLanguage} ps="4" pe="3" py="2" color="green.400" align="center">
          {getLanguageName(selectedLanguage)}
        </Flex>
      )}
    </Flex>
  );
}

const getLanguageName = (locale: string) => new Intl.DisplayNames([locale], {type: 'language'}).of(locale);

const LanguageSwitch = (props: ChooseLanguageProps) => {
  const onLanguageChange = (e: MouseEvent<HTMLButtonElement>) => {
    const newLanguage = e.currentTarget.value as Language;

    mixpanel?.track('Trivia Challenge Language Changed', {
      from: props.selectedLanguage,
      to: newLanguage,
    });

    props.onLanguageChange(newLanguage);
  };

  return (
    <Flex maxW="full" minW="0" fontSize="sm" fontWeight="700" rounded="md">
      <Menu matchWidth autoSelect={false} gutter={4}>
        {({isOpen}) => (
          <>
            <MenuButton
              as={Button}
              textAlign="start"
              fontSize="inherit"
              fontWeight="inherit"
              w="full"
              h="2.75rem"
              ps="4"
              pe="3"
              py="2"
              rounded="inherit"
              borderWidth="1px"
              borderColor="gray.500"
              color="green.400"
              bgColor="#f7f7f7"
              _hover={{borderColor: 'gray.300'}}
              _active={{}}
              rightIcon={<ArrowDownIcon transform="auto" scaleY={isOpen ? -1 : 1} transition="transform 0.3s" />}
            >
              {getLanguageName(props.selectedLanguage)}
            </MenuButton>

            <MenuList py="2.5" minW="unset" shadow="xs" rounded="sm" zIndex="dropdown" maxH="50vh" overflow="auto">
              {props.languages.map((language) => (
                <MenuItem key={language} p="0" value={language} onClick={onLanguageChange}>
                  <Button
                    as="span"
                    lang={language}
                    display="flex"
                    justifyContent="flex-start"
                    gap="4"
                    variant="unstyled"
                    rounded="none"
                    w="full"
                    h="fit-content"
                    paddingInline="4"
                    py="1.5"
                    fontSize="inherit"
                    fontWeight="inherit"
                    isActive={language === props.selectedLanguage}
                    _active={{bgColor: 'gray.400', fontWeight: 700}}
                    _hover={{bgColor: 'gray.600'}}
                  >
                    {getLanguageName(language)}
                  </Button>
                </MenuItem>
              ))}
            </MenuList>
          </>
        )}
      </Menu>
    </Flex>
  );
};

const ArrowDownIcon = (props: IconProps) => (
  <Icon width="15px" height="8px" viewBox="0 0 15 8" fill="none" {...props}>
    <path
      d="M13.42 0.18H7.18999H1.57999C0.619992 0.18 0.139993 1.34 0.819993 2.02L5.99999 7.2C6.82999 8.03 8.17999 8.03 9.00999 7.2L10.98 5.23L14.19 2.02C14.86 1.34 14.38 0.18 13.42 0.18Z"
      fill="#2a2e3a"
    />
  </Icon>
);
